import { fitWithin } from "@scheduler/shared";
import type { ReportImageType } from "@scheduler/shared";

/**
 * Screenshots in a report (Jeremy, 3 Nov 2026): pasted, dropped or picked, checked against the
 * limits (`imageRefusal` in shared/src/images.ts), then SHRUNK in the browser before they are
 * sent -- a full-screen capture is several MB as a PNG and a few hundred KB as a WebP 1600px
 * wide, which is all a report needs to show what was on the screen. Canopy's
 * web/src/noteImages.ts, the same code.
 */

/** An image waiting in the report, shrunk and ready to send. */
export interface PendingImage {
  /** Local only, for React and for removing it. */
  readonly key: string;
  readonly contentType: ReportImageType;
  readonly blob: Blob;
  /** An object URL for the thumbnail; revoke it when the image goes. */
  readonly url: string;
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, 0.85));
}

/**
 * The image redrawn at most 1600px, as WebP -- or JPEG where the browser cannot write WebP (it
 * then hands back a PNG, which is the wrong trade for a photo). Refuses an image the browser
 * cannot open.
 */
export async function shrink(file: Blob): Promise<{ contentType: ReportImageType; blob: Blob }> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("That image could not be opened.");
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("That image could not be opened.");
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  for (const type of ["image/webp", "image/jpeg"] as const) {
    const blob = await toBlob(canvas, type);
    if (blob && blob.type === type) return { contentType: type, blob };
  }
  throw new Error("That image could not be opened.");
}

/** Base64 without the "data:...;base64," prefix, as the API takes it. */
export function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(new Error("That image could not be read."));
    reader.readAsDataURL(blob);
  });
}

/** The image files in a paste or a drop, in order. */
export function imageFiles(list: DataTransferItemList | FileList | null | undefined): File[] {
  if (!list) return [];
  const out: File[] = [];
  for (const entry of Array.from(list as ArrayLike<DataTransferItem | File>)) {
    const file = "getAsFile" in entry ? (entry.kind === "file" ? entry.getAsFile() : null) : entry;
    if (file && file.type.startsWith("image/")) out.push(file);
  }
  return out;
}
